import { ContactFormValue, RiderFormValues } from '../types';

// PhoneNumberInput (@hopskipdrive/react-components) only strips a single leading
// character from its initialValue, so it expects a bare 10-digit number, not the
// E.164 string ("+1XXXXXXXXXX") the API returns — strip the country code here.
const stripCountryCode = (phoneNumber: string): string => phoneNumber.replace(/^\+?1/, '');

const mapRiderContactsToFormValues = (
  passengersContacts: RidersContact[] | undefined
): ContactFormValue[] | undefined => {
  if (!passengersContacts || passengersContacts.length === 0) {
    return undefined;
  }

  // The API doesn't guarantee the caregiver contact is returned first — row 0 is
  // rendered as a locked, disabled "Caregiver" field (see RoleField), so the caregiver
  // contact must be sorted into position 0 rather than assumed to already be there.
  // Sort is stable, so relative order of non-caregiver contacts is preserved.
  const sorted = [...passengersContacts].sort((a, b) => {
    if (a.role === 'caregiver' && b.role !== 'caregiver') {
      return -1;
    }
    if (a.role !== 'caregiver' && b.role === 'caregiver') {
      return 1;
    }

    return 0;
  });

  return sorted.map((c) => ({
    id: c.id,
    firstName: c.firstName ?? '',
    lastName: c.lastName ?? '',
    role: c.role === 'emergency_contact' ? 'emergency_contact' : 'caregiver',
    relationship: c.relationship ?? '',
    phoneNumber: c.phoneNumber ? stripCountryCode(c.phoneNumber) : '',
    email: c.email ?? '',
  }));
};

export const mapRiderToFormValues = (rider: RiderDetails | undefined): Partial<RiderFormValues> => {
  if (!rider) {
    return {};
  }

  const contacts = mapRiderContactsToFormValues(rider.passengersContacts);

  return {
    // Rider Services
    codeWord: rider.codeWord ?? '',
    boosterSeat: rider.boosterSeat ?? false,
    frontSeatAuthorized: rider.frontSeatAuthorized ?? false,
    riderNotes: rider.riderNotes ?? '',
    dateOfBirth: rider.dateOfBirth ? new Date(rider.dateOfBirth) : null,
    services: rider.services?.map((s) => s.code) ?? [],
    serviceConflictMessages: [],
    supportSettings:
      rider.supportSettings?.map((s) => ({
        supportType: s.supportType,
        details: s.details ?? '',
        selected: true,
      })) ?? [],
    // Basic Info — Identity & contact
    firstName: rider.firstName ?? '',
    lastName: rider.lastName ?? '',
    gender: rider.gender?.replace('-', '_') ?? '',
    email: rider.email ?? null,
    mobilePhoneNumber: rider.mobilePhoneNumber ? stripCountryCode(rider.mobilePhoneNumber) : '',
    photosAttributes: rider.profilePhotoUrl
      ? [{ url: rider.profilePhotoUrl, attachment: undefined }]
      : [{ attachment: undefined }],
    // Basic Info — Account & billing
    category: rider.category ?? null,
    externalId: rider.externalId ?? '',
    invoiceGroupId: rider.invoiceGroup?.id ?? null,
    invoiceGroup: rider.invoiceGroup ?? null,
    // Basic Info — Toggle cards
    isAide: rider.isAide ?? false,
    ...(rider.sharedBillingRider
      ? {
          isSharedBillingRider: true,
          sharedBillingRider: {
            residenceAccountId: rider.sharedBillingRider.residenceAccountId,
            residenceAccountContactName: rider.sharedBillingRider.residenceAccountContactName,
            residenceAccountContactId: rider.sharedBillingRider.residenceAccountContactId,
            residenceAccountContactEmail: rider.sharedBillingRider.residenceAccountContactEmail,
          },
        }
      : {
          isSharedBillingRider: false,
          sharedBillingRider: {
            residenceAccountId: null,
            residenceAccountContactName: '',
            residenceAccountContactEmail: '',
          },
        }),
    // Contacts
    ...(contacts ? { contacts } : {}),
    originalContactIds: contacts?.map((c) => c.id).filter((id): id is number => id !== undefined) ?? [],
  };
};
